import { useEffect } from 'react';
import { clsx } from 'clsx';
import { Button, Flex, Text } from '@nula/core-ui';
import { useOnboardingPermissions } from '@/lib/permissions/use-onboarding-permissions';
import type { PermStatus, AxStatus } from '@/lib/permissions/use-onboarding-permissions';
import styles from './permissions-step.module.css';

export interface PermissionsStepProps {
  /** Called after each permission attempt (reserved for M5 analytics). */
  onPermissionResult: (kind: PermissionKind, granted: boolean) => void;
  /** Called when the user clicks Allow for a permission (reserved for M5 analytics). */
  onPermissionPrompted: (kind: PermissionKind) => void;
  onContinue: () => void;
  onSkip: () => void;
}

export type PermissionKind = 'microphone' | 'system_audio' | 'accessibility';

// SVG icons — inline SVG children are an allowed exception per CLAUDE.md.
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <polyline points="2,8 6,12 14,4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const AudioIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 5 6 9H2v6h4l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M19 5a9 9 0 0 1 0 14" />
  </svg>
);

const AccessibilityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="4" r="2" />
    <path d="M19 8.5c-2 1-4.5 1.5-7 1.5s-5-.5-7-1.5" />
    <path d="M12 10v5" />
    <path d="m9 21 3-6 3 6" />
  </svg>
);

function isGranted(status: PermStatus | null): boolean {
  return status === 'granted';
}

function isAxGranted(status: AxStatus): boolean {
  return status === 'granted';
}

interface RowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  granted: boolean;
  testId: string;
  onAllow: () => void;
}

function PermissionRow({ icon, title, description, granted, testId, onAllow }: RowProps) {
  return (
    <Flex align="center" gap="md" className={clsx(styles.permissionRow, granted && styles.permissionRowGranted)}>
      <span className={styles.permissionIcon}>{icon}</span>
      <Flex vertical gap="xs" className={styles.permissionInfo}>
        <Text className={styles.permissionTitle}>{title}</Text>
        <Text type="secondary" size="sm" className={styles.permissionDesc}>{description}</Text>
      </Flex>
      {granted ? (
        <Flex align="center" gap="xs" className={styles.grantedBadge} data-testid={testId}>
          <CheckIcon />
          Granted
        </Flex>
      ) : (
        <Button variant="default" size="sm" onClick={onAllow} className={styles.allowBtn}>Allow</Button>
      )}
    </Flex>
  );
}

export function PermissionsStep({
  onPermissionResult,
  onPermissionPrompted,
  onContinue,
  onSkip,
}: PermissionsStepProps) {
  const { micStatus, screenStatus, axStatus, requestMic, requestScreen, requestAccessibility } =
    useOnboardingPermissions();

  // Report already-granted permissions up to the shell when live statuses
  // resolve, so the right-side glyphs reflect grants the user made on a
  // previous visit (not just ones granted during this session).
  useEffect(() => {
    if (isGranted(micStatus)) onPermissionResult('microphone', true);
  }, [micStatus, onPermissionResult]);
  useEffect(() => {
    if (isGranted(screenStatus)) onPermissionResult('system_audio', true);
  }, [screenStatus, onPermissionResult]);
  useEffect(() => {
    if (isAxGranted(axStatus)) onPermissionResult('accessibility', true);
  }, [axStatus, onPermissionResult]);

  async function handleMic() {
    onPermissionPrompted('microphone');
    const status = await requestMic();
    onPermissionResult('microphone', isGranted(status));
  }

  async function handleScreen() {
    onPermissionPrompted('system_audio');
    const status = await requestScreen();
    onPermissionResult('system_audio', isGranted(status));
  }

  async function handleAccessibility() {
    onPermissionPrompted('accessibility');
    const result = await requestAccessibility();
    onPermissionResult('accessibility', result === 'granted');
  }

  return (
    <Flex vertical gap="lg">
      <Flex vertical>
        <Text className={styles.eyebrow}>Quick setup</Text>
        <Text className={styles.title}>
          Give Nula what it <em className={styles.titleEm}>needs.</em>
        </Text>
        <Text type="secondary" className={styles.subtitle}>
          To capture meetings end-to-end, Nula needs a few system permissions. Nothing records until you press Record.
        </Text>
      </Flex>

      <Flex vertical gap="sm">
        <PermissionRow
          icon={<MicIcon />}
          title="Microphone"
          description="Captures your voice during meetings so Nula can write your notes."
          granted={isGranted(micStatus)}
          testId="mic-granted"
          onAllow={() => void handleMic()}
        />
        <PermissionRow
          icon={<AudioIcon />}
          title="System audio"
          description="Hears the other side of your calls. Audio only, never video."
          granted={isGranted(screenStatus)}
          testId="screen-granted"
          onAllow={() => void handleScreen()}
        />
        <PermissionRow
          icon={<AccessibilityIcon />}
          title="Accessibility"
          description="Lets Nula detect when a meeting starts so recording can begin on its own."
          granted={isAxGranted(axStatus)}
          testId="ax-granted"
          onAllow={() => void handleAccessibility()}
        />
      </Flex>

      <Flex align="center" gap="md">
        <Button variant="primary" size="lg" block onClick={onContinue}>Continue</Button>
        <Button variant="text" size="sm" onClick={onSkip} className={styles.skip}>Skip</Button>
      </Flex>
    </Flex>
  );
}
